import * as _ from 'lodash';
import { remunerationTypes } from '../../objects/Payslip';
import { PaymentGroupModel, comparePaymentModel, paymentGroupInstanceTypes } from '../PaymentGroupModel';
import { PaymentModel } from '../PaymentModel';
import { StandardCalculationContext } from '../calculation/StandardCalculator';
import { TimeString } from '../../misc/TimeString';

/**
 * @typedef { import('../PayslipModel').PayslipModel } PayslipModel
 * @typedef { import('../calculation/StandardCalculator').StandardCalculator } StandardCalculator
 */

const proposedState = Object.freeze({
    initial: 0,
    accepted: 1,
    rejecting: 2,
    rejected: 3,
});

export class ProposedTimeGroupModel extends PaymentGroupModel {

    /**
     * @param {PayslipModel} payslipModel
     */
    constructor(payslipModel) {
        super(payslipModel);

        this.presentTime = payslipModel.attendance.TimeReport.PresentTime;
        this.servingTime = payslipModel.attendance.TimeReport.ServingTime;

        const presentTimeString = TimeString.parse(this.presentTime);
        const meetingTimeString = TimeString.parse(payslipModel.attendance.Meeting.MeetingTime);
        this.isPresentOutsideMeeting = presentTimeString && (meetingTimeString == null || !meetingTimeString.contains(presentTimeString));
        
        this._state = proposedState.initial;
        this.rejectionMessage = '';
    }
    
    get paymentGroupInstanceType() {
        return paymentGroupInstanceTypes.proposedTime;
    }

    checkChanges() {
        return this.hasResponded;
    }

    get isInitial() {
        return this._state === proposedState.initial;
    }

    get hasAccepted() {
        return this._state === proposedState.accepted;
    }

    get hasRejected() {
        return this._state === proposedState.rejected;
    }

    get isRejecting() {
        return this._state === proposedState.rejecting;
    }

    get hasResponded() {
        return this.hasAccepted || this.hasRejected;
    }

    get canAccept() {
        return this.availableEmployers == null || this.selectedEmployer != null;
    }

    _setState(state) {
        if (this._state !== state) {
            this._state = state;
            this.payslipModel.paymentGroupChanged(this);
        }
    }

    accept() {
        this._setState(proposedState.accepted);
        this.payslipModel.proposedTimeChanged(this, true);
    }

    rejecting() {
        this._setState(proposedState.rejecting);
    }

    rejected() {
        this._setState(proposedState.rejected);
    }

    reset() {
        this._setState(proposedState.initial);
        this.payslipModel.proposedTimeChanged(this, false);
    }

    createAdd() {
        if (!this.hasAccepted) return null;

        let [travelPayments, meetingPayments] = _.partition(this.payments, x => x.isAutoTravel);

        let result = [];

        if (meetingPayments.length > 0) {
            result.push({
                RemunerationType: remunerationTypes.calculatedMeeting,
                AcceptsClaim: null,
                WageCalculationId: this.wageCalculation.Id,
                Payments: _.map(meetingPayments, x => x.payment),
            });
        }

        if (travelPayments.length > 0) {
            result.push({
                RemunerationType: remunerationTypes.calculatedTravel,
                AcceptsClaim: null,
                WageCalculationId: this.wageCalculation.Id,
                Payments: _.map(travelPayments, x => x.payment),
            });
        }
        
        return result;
    }

    /**
     * @param {PayslipModel} payslipModel
     * @param {StandardCalculator} calculator
     */
    static create(payslipModel, calculator) {

        let result = new ProposedTimeGroupModel(payslipModel);
        
        if (calculator != null) {
            let ctx = new StandardCalculationContext(payslipModel.attendance);
            ctx.presentTime = payslipModel.attendance.TimeReport.PresentTime;
            ctx.servingTime = payslipModel.attendance.TimeReport.ServingTime;
            
            let calculatedMeeting = calculator.calculateMeeting(ctx);
            let meetingPayments = _.map(calculatedMeeting, x => PaymentModel.fromCalculated(result, x));
            meetingPayments.sort(comparePaymentModel);

            let calculatedTravel = calculator.calculateTravel(ctx);
            let travelPayments = _.map(calculatedTravel, x => PaymentModel.fromCalculated(result, x));
            _.forEach(travelPayments, x => { x.isAutoTravel = true; });
            travelPayments.sort(comparePaymentModel);

            result.payments = _.concat(meetingPayments, travelPayments);
            result.wageCalculation = calculator.rules.WageCalculation;
        }

        result.sortPayments();

        return result;
    }
}
